/**
 * 영업시간 관리 헬퍼 함수 라이브러리
 * restaurant_operating_hours 테이블과 JSONB business_hours 구조 통합 관리
 */

import { getKoreanDay, getDayName, getKoreanTime, isTimeInBreak } from './time';
import type { 
  OrderType, 
  Restaurant, 
  RestaurantWithProduct,
  OperatingHours, 
  BusinessHours 
} from '../types';

// Re-export types for compatibility
export type { OrderType, Restaurant, OperatingHours, BusinessHours };

// =====================================
// 추가 타입 정의
// =====================================

export interface TodayOperatingHours {
  dayName: string;
  openTime?: string;
  closeTime?: string;
  breakStartTime?: string;
  breakEndTime?: string;
  isClosed: boolean;
}

// =====================================
// 변환 함수
// =====================================

/**
 * JSONB business_hours에서 정규화된 구조로 변환
 */
export function convertBusinessHoursToNormalized(businessHours: BusinessHours): OperatingHours[] {
  const dayNames = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'];
  const operatingHours: OperatingHours[] = [];

  dayNames.forEach((dayName, dayIndex) => {
    const dayData = businessHours[dayName] || businessHours[dayIndex.toString()];
    
    operatingHours.push({
      day_of_week: dayIndex,
      is_closed: dayData?.is_closed || false,
      open_time: dayData?.open_time,
      close_time: dayData?.close_time,
      break_start_time: dayData?.break_start_time,
      break_end_time: dayData?.break_end_time
    });
  });

  return operatingHours;
}

/**
 * 정규화된 구조를 JSONB business_hours로 변환
 */
export function convertNormalizedToBusinessHours(hours: OperatingHours[]): BusinessHours {
  const businessHours: BusinessHours = {};
  const dayNames = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'];

  hours.forEach((hour) => {
    const dayName = dayNames[hour.day_of_week];
    if (!dayName) return;

    businessHours[dayName] = {
      is_closed: hour.is_closed,
      open_time: hour.open_time,
      close_time: hour.close_time,
      break_start_time: hour.break_start_time,
      break_end_time: hour.break_end_time
    };
  });

  return businessHours;
}

// =====================================
// 통합 조회 함수
// =====================================

/**
 * 오늘의 영업시간 정보를 가져오는 통합 함수
 * restaurant_operating_hours 테이블 우선, JSONB business_hours 폴백, 개별 컬럼 최종 폴백
 */
export function getTodayOperatingHours(restaurant: Restaurant): TodayOperatingHours {
  const today = getKoreanDay();
  const dayName = getDayName(today);

  // 1. RPC에서 전달된 operating_hours 배열을 최우선으로 사용 (가장 신뢰도 높은 소스)
  if (restaurant.operating_hours && Array.isArray(restaurant.operating_hours) && restaurant.operating_hours.length > 0) {
    const todayHours = restaurant.operating_hours.find(hours => hours.day_of_week === today);
    
    if (todayHours) {
      return {
        dayName,
        openTime: formatTimeToHHMM(todayHours.open_time),
        closeTime: formatTimeToHHMM(todayHours.close_time),
        breakStartTime: formatTimeToHHMM(todayHours.break_start_time),
        breakEndTime: formatTimeToHHMM(todayHours.break_end_time),
        isClosed: todayHours.is_closed
      };
    }
  }

  // 2. Fallback: business_hours JSON 객체 확인 (기존 데이터 호환성)
  const dayNames = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'];
  const todayName = dayNames[today];
  if (restaurant.business_hours) {
    const todayData = restaurant.business_hours[todayName] || restaurant.business_hours[today.toString()];
    if (todayData) {
      return {
        dayName,
        openTime: formatTimeToHHMM(todayData.open_time),
        closeTime: formatTimeToHHMM(todayData.close_time),
        breakStartTime: formatTimeToHHMM(todayData.break_start_time),
        breakEndTime: formatTimeToHHMM(todayData.break_end_time),
        isClosed: todayData.is_closed || false
      };
    }
  }

  // 3. 이미 계산된 todayOperatingHours 사용 (시간 포맷팅 적용)
  if (restaurant.todayOperatingHours) {
    return {
      ...restaurant.todayOperatingHours,
      openTime: formatTimeToHHMM(restaurant.todayOperatingHours.openTime),
      closeTime: formatTimeToHHMM(restaurant.todayOperatingHours.closeTime),
      breakStartTime: formatTimeToHHMM(restaurant.todayOperatingHours.breakStartTime),
      breakEndTime: formatTimeToHHMM(restaurant.todayOperatingHours.breakEndTime)
    };
  }

  // 4. 개별 컬럼에서 오늘의 영업시간 추출 (실제 DB 구조에 맞는 폴백)
  const startTimeKey = `${todayName}_start` as keyof typeof restaurant;
  const endTimeKey = `${todayName}_end` as keyof typeof restaurant;
  const breakStartKey = `${todayName}_break_start` as keyof typeof restaurant;
  const breakEndKey = `${todayName}_break_end` as keyof typeof restaurant;
  const isClosedKey = `is_closed_${todayName}` as keyof typeof restaurant;

  const openTime = restaurant[startTimeKey] as string | undefined;
  const closeTime = restaurant[endTimeKey] as string | undefined;
  const breakStartTime = restaurant[breakStartKey] as string | undefined;
  const breakEndTime = restaurant[breakEndKey] as string | undefined;
  const isClosed = restaurant[isClosedKey] as boolean | undefined;

  // 개별 컬럼에 데이터가 있으면 사용 (시간 포맷팅 적용)
  if (openTime || closeTime || isClosed !== undefined) {
    return {
      dayName,
      openTime: formatTimeToHHMM(openTime),
      closeTime: formatTimeToHHMM(closeTime),
      breakStartTime: formatTimeToHHMM(breakStartTime),
      breakEndTime: formatTimeToHHMM(breakEndTime),
      isClosed: isClosed || false
    };
  }

  // 5. 기본값 반환 (영업시간 정보가 없는 경우)
  return {
    dayName,
    openTime: undefined,
    closeTime: undefined,
    breakStartTime: undefined,
    breakEndTime: undefined,
    isClosed: false
  };
}

/**
 * 내일의 영업시간 정보를 가져오는 통합 함수
 */
export function getTomorrowOperatingHours(restaurant: Restaurant): TodayOperatingHours {
  const today = getKoreanDay();
  const tomorrow = (today + 1) % 7;
  const dayName = getDayName(tomorrow);

  // 1. RPC에서 전달된 operating_hours 배열을 최우선으로 사용 (가장 신뢰도 높은 소스)
  if (restaurant.operating_hours && Array.isArray(restaurant.operating_hours) && restaurant.operating_hours.length > 0) {
    const tomorrowHours = restaurant.operating_hours.find(hours => hours.day_of_week === tomorrow);
    if (tomorrowHours) {
      return {
        dayName,
        openTime: formatTimeToHHMM(tomorrowHours.open_time),
        closeTime: formatTimeToHHMM(tomorrowHours.close_time),
        breakStartTime: formatTimeToHHMM(tomorrowHours.break_start_time),
        breakEndTime: formatTimeToHHMM(tomorrowHours.break_end_time),
        isClosed: tomorrowHours.is_closed
      };
    }
  }

  // 2. Fallback: business_hours JSON 객체 확인 (기존 데이터 호환성)
  const dayNames = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'];
  const tomorrowName = dayNames[tomorrow];
  if (restaurant.business_hours) {
    const tomorrowData = restaurant.business_hours[tomorrowName] || restaurant.business_hours[tomorrow.toString()];
    if (tomorrowData) {
      return {
        dayName,
        openTime: formatTimeToHHMM(tomorrowData.open_time),
        closeTime: formatTimeToHHMM(tomorrowData.close_time),
        breakStartTime: formatTimeToHHMM(tomorrowData.break_start_time),
        breakEndTime: formatTimeToHHMM(tomorrowData.break_end_time),
        isClosed: tomorrowData.is_closed || false
      };
    }
  }

  // 3. 기본값 반환 (영업시간 정보가 없는 경우)
  return {
    dayName,
    openTime: undefined,
    closeTime: undefined,
    breakStartTime: undefined,
    breakEndTime: undefined,
    isClosed: false
  };
}

/**
 * 모든 요일의 영업시간 정보를 가져오는 통합 함수
 */
export function getAllOperatingHours(restaurant: Restaurant): OperatingHours[] {
  const normalizedSource: OperatingHours[] = restaurant.operating_hours && restaurant.operating_hours.length > 0
    ? restaurant.operating_hours
    : restaurant.business_hours
      ? convertBusinessHoursToNormalized(restaurant.business_hours)
      : [];

  const hoursByDay = new Map<number, OperatingHours>();

  normalizedSource.forEach((hours) => {
    if (typeof hours?.day_of_week !== 'number') {
      return;
    }

    hoursByDay.set(hours.day_of_week, {
      day_of_week: hours.day_of_week,
      is_closed: Boolean(hours.is_closed),
      open_time: hours.open_time || undefined,
      close_time: hours.close_time || undefined,
      break_start_time: hours.break_start_time || undefined,
      break_end_time: hours.break_end_time || undefined
    });
  });

  return Array.from({ length: 7 }, (_, index) => {
    const existing = hoursByDay.get(index);

    if (existing) {
      return existing;
    }

    return {
      day_of_week: index,
      is_closed: true,
      open_time: undefined,
      close_time: undefined,
      break_start_time: undefined,
      break_end_time: undefined
    };
  });
}

// =====================================
// 주문 가능성 판단 함수
// =====================================

/**
 * 통합된 주문 가능성 판단 함수
 * 기존의 useOrderAvailability와 useStoreFiltering 로직 통합
 */
export function checkOrderAvailability(
  restaurant: Restaurant, 
  orderType: OrderType,
  currentTime?: string
): boolean {
  const now = currentTime || getKoreanTime();
  const currentMinutes = timeToMinutes(now);

  // 매장 마감할인 토글이 꺼져 있으면 DEADLINE_DISCOUNT는 주문 불가 처리
  if (orderType === 'DEADLINE_DISCOUNT' && (restaurant as any).deadline_discount_enabled === false) {
    return false;
  }

  // 예약할인은 영업시간과 무관하게 별도 처리
  if (orderType === 'RESERVATION_DISCOUNT') {
    return checkReservationDiscountAvailability(restaurant, currentMinutes);
  }
  
  // 나머지 주문 유형은 기존 로직 유지
  // 기본 조건 확인
  if (!restaurant.is_open) {
    return false;
  }

  const todayHours = getTodayOperatingHours(restaurant);
  
  if (todayHours.isClosed) {
    return false;
  }

  // 영업시간이 없으면 기본적으로 영업 중 상태 반환
  if (!todayHours.openTime || !todayHours.closeTime) {
    return restaurant.is_open;
  }

  const openMinutes = timeToMinutes(todayHours.openTime);
  const closeMinutes = timeToMinutes(todayHours.closeTime);

  // 브레이크 타임 체크
  if (isTimeInBreak(now, todayHours.breakStartTime, todayHours.breakEndTime)) {
    return false;
  }

  let result = false;
  
  // 주문 타입별 가능성 판단
  switch (orderType) {
    case 'DEADLINE_DISCOUNT':
      result = checkDeadlineDiscountAvailability(restaurant, currentMinutes, closeMinutes);
      break;
    
    case 'REGULAR_ORDER':
      result = currentMinutes >= openMinutes && currentMinutes < closeMinutes;
      break;
    
    default:
      result = true;
  }
  
  return result;
}

/**
 * 마감할인 주문 가능성 판단
 * 개선: Restaurant 테이블을 단일 진실 공급원으로 사용, Product Offers는 동기화된 데이터로 사용
 */
function checkDeadlineDiscountAvailability(
  restaurant: Restaurant | RestaurantWithProduct,
  currentMinutes: number,
  closeMinutes: number
): boolean {
  // 1. Restaurant 테이블의 마감할인 시간 사용 (단일 진실 공급원)
  if (restaurant.deadline_discount_start_time && restaurant.deadline_discount_end_time) {
    const deadlineStartMinutes = timeToMinutes(restaurant.deadline_discount_start_time);
    const deadlineEndMinutes = timeToMinutes(restaurant.deadline_discount_end_time);
    
    return currentMinutes >= deadlineStartMinutes && currentMinutes <= deadlineEndMinutes;
  }

  // 2. Fallback: Product Offers 데이터에서 당일 등록된 마감할인 시간 확인 (동기화 검증용)
  if ('products' in restaurant && restaurant.products && restaurant.products.length > 0) {
    const today = new Date().toISOString().split('T')[0];
    
    // 당일 등록된 활성 마감할인 오퍼 찾기
    const todayDeadlineOffers = restaurant.products
      .flatMap((product: any) => product.offers || [])
      .filter((offer: any) => {
        if (offer.offer_type !== 'DEADLINE_DISCOUNT' || !offer.is_active) return false;
        
        // 당일 등록 검증: valid_from이 오늘이거나, valid_from이 없으면 기본 당일로 간주
        if (offer.valid_from) {
          const validFromDate = new Date(offer.valid_from).toISOString().split('T')[0];
          return validFromDate === today;
        }
        
        // valid_from이 없으면 당일 등록으로 간주 (기존 데이터 호환성)
        return true;
      });
    
    // 가장 이른 주문 시작 시간과 가장 늦은 주문 종료 시간 사용 (fallback)
    if (todayDeadlineOffers.length > 0) {
      let earliestOrderStart: number | null = null;
      let latestOrderEnd: number | null = null;
      
      for (const offer of todayDeadlineOffers) {
        if (offer.deadline_order_start_time && offer.deadline_order_end_time) {
          const orderStartMinutes = timeToMinutes(offer.deadline_order_start_time);
          const orderEndMinutes = timeToMinutes(offer.deadline_order_end_time);
          
          if (earliestOrderStart === null || orderStartMinutes < earliestOrderStart) {
            earliestOrderStart = orderStartMinutes;
          }
          if (latestOrderEnd === null || orderEndMinutes > latestOrderEnd) {
            latestOrderEnd = orderEndMinutes;
          }
        }
      }
      
      if (earliestOrderStart !== null && latestOrderEnd !== null) {
        return currentMinutes >= earliestOrderStart && currentMinutes <= latestOrderEnd;
      }
    }
  }

  // 3. 기본값: 마감할인 시간이 설정되지 않은 경우, 영업시간 중에는 항상 주문 가능 (최종 fallback)
  // 실제 마감할인은 영업 중 언제든 주문할 수 있어야 함
  return currentMinutes < closeMinutes;
}

/**
 * 예약할인 주문 가능성 판단
 * 예약할인은 익일 수령이므로 내일의 영업시간을 기준으로 판단
 * closeMinutes 매개변수 제거 - 오늘 영업시간을 직접 조회
 */
function checkReservationDiscountAvailability(
  restaurant: Restaurant,
  currentMinutes: number
): boolean {
  // 예약할인 지원 여부 확인
  if (!restaurant.reservation_discount_enabled) return false;
  
  // 내일의 영업시간 확인 (예약할인은 익일 수령)
  const tomorrowHours = getTomorrowOperatingHours(restaurant);
  
  // 내일이 휴무이면 예약할인 불가능
  if (tomorrowHours.isClosed || !tomorrowHours.openTime || !tomorrowHours.closeTime) {
    return false;
  }
  
  // 오늘의 영업시간 가져오기 (휴무일이어도 가져옴)
  const todayHours = getTodayOperatingHours(restaurant);
  
  // 예약 가능 시간 처리
  // reservation_available_start가 있으면 사용, 없으면 00:00
  const reservationStartMinutes = restaurant.reservation_available_start 
    ? timeToMinutes(restaurant.reservation_available_start) 
    : 0;
  
  // reservation_available_end가 있으면 사용, 없으면 오늘 영업종료 1시간 전
  const reservationEndMinutes = restaurant.reservation_available_end
    ? timeToMinutes(restaurant.reservation_available_end)
    : todayHours.closeTime 
      ? timeToMinutes(todayHours.closeTime) - 60
      : 1200; // 기본값 20:00 (영업시간 정보가 없는 경우)
  
  return currentMinutes >= reservationStartMinutes && currentMinutes < reservationEndMinutes;
}

// =====================================
// 유틸리티 함수
// =====================================

/**
 * 시간 문자열을 HH:MM 형식으로 변환
 * HH:MM:SS → HH:MM, HH:MM → HH:MM (그대로), null/undefined → null
 */
function formatTimeToHHMM(timeString?: string | null): string | undefined {
  if (!timeString) return undefined;
  
  try {
    // 이미 HH:MM 형식인지 확인
    if (timeString.match(/^\d{2}:\d{2}$/)) {
      return timeString;
    }
    
    // HH:MM:SS 형식인 경우 초 부분 제거
    if (timeString.includes(':')) {
      const timeParts = timeString.split(':');
      if (timeParts.length >= 2) {
        return `${timeParts[0]}:${timeParts[1]}`;
      }
    }
    
    // 예상치 못한 형식인 경우 원본 반환
    return timeString;
  } catch (error) {
    return timeString;
  }
}

/**
 * 시간 문자열을 분 단위로 변환 (HH:MM 형식)
 */
function timeToMinutes(timeStr: string): number {
  const [hours, minutes] = timeStr.split(':').map(Number);
  return hours * 60 + minutes;
}

/**
 * 분을 시간 문자열로 변환 (HH:MM 형식)
 */
export function minutesToTime(minutes: number): string {
  const hours = Math.floor(minutes / 60);
  const mins = minutes % 60;
  return `${hours.toString().padStart(2, '0')}:${mins.toString().padStart(2, '0')}`;
}

/**
 * 특정 요일의 영업시간 정보 가져오기
 */
export function getOperatingHoursForDay(
  restaurant: Restaurant, 
  dayOfWeek: number
): TodayOperatingHours {
  const dayNames = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'];
  const dayName = getDayName(dayOfWeek);
  const todayName = dayNames[dayOfWeek];

  // 1. 정규화된 restaurant_operating_hours 테이블 우선 사용
  if (restaurant.operating_hours && restaurant.operating_hours.length > 0) {
    const dayHours = restaurant.operating_hours.find(hours => hours.day_of_week === dayOfWeek);
    if (dayHours) {
      return {
        dayName,
        openTime: formatTimeToHHMM(dayHours.open_time),
        closeTime: formatTimeToHHMM(dayHours.close_time),
        breakStartTime: formatTimeToHHMM(dayHours.break_start_time),
        breakEndTime: formatTimeToHHMM(dayHours.break_end_time),
        isClosed: dayHours.is_closed
      };
    }
  }

  // 2. JSONB business_hours 폴백
  if (restaurant.business_hours) {
    const dayData = restaurant.business_hours[todayName] || restaurant.business_hours[dayOfWeek.toString()];
    if (dayData) {
      return {
        dayName,
        openTime: formatTimeToHHMM(dayData.open_time),
        closeTime: formatTimeToHHMM(dayData.close_time),
        breakStartTime: formatTimeToHHMM(dayData.break_start_time),
        breakEndTime: formatTimeToHHMM(dayData.break_end_time),
        isClosed: dayData.is_closed || false
      };
    }
  }

  // 3. 기본값 반환 (영업시간 정보가 없는 경우)
  return {
    dayName,
    openTime: undefined,
    closeTime: undefined,
    breakStartTime: undefined,
    breakEndTime: undefined,
    isClosed: false
  };
}

/**
 * 영업시간 정보가 유효한지 확인
 */
export function isValidOperatingHours(hours: TodayOperatingHours): boolean {
  if (hours.isClosed) return true; // 휴무일은 유효
  
  // 영업시간이 설정되어 있는지 확인
  if (!hours.openTime || !hours.closeTime) return false;
  
  const openMinutes = timeToMinutes(hours.openTime);
  const closeMinutes = timeToMinutes(hours.closeTime);
  
  // 시작 시간이 종료 시간보다 빠른지 확인
  return openMinutes < closeMinutes;
}

/**
 * 영업시간 요약 정보 생성
 */
export function generateOperatingHoursSummary(restaurant: Restaurant): string {
  const todayHours = getTodayOperatingHours(restaurant);
  
  if (todayHours.isClosed) {
    return '휴무';
  }
  
  if (!todayHours.openTime || !todayHours.closeTime) {
    return restaurant.is_open ? '영업 중' : '영업종료';
  }
  
  let summary = `${todayHours.openTime} - ${todayHours.closeTime}`;
  
  if (todayHours.breakStartTime && todayHours.breakEndTime) {
    summary += ` (브레이크타임: ${todayHours.breakStartTime} - ${todayHours.breakEndTime})`;
  }
  
  return summary;
}
